import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, addDays, isWeekend, getDay } from 'date-fns';
import { bookingAPI } from '../services/api';
import { 
  Calendar as CalendarIcon, Clock, User, Mail, Phone, 
  Loader2, CheckCircle, Play, Video
} from 'lucide-react';

const TeacherBookingModal = ({ teacher, open, onClose }) => {
  const { language } = useLanguage();
  const [step, setStep] = useState(1); // 1: Video/Intro, 2: Select Date/Time, 3: Contact Info, 4: Success
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  
  // Form state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [studentName, setStudentName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [studentPhone, setStudentPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [sessionType, setSessionType] = useState('trial');
  const [bookingResult, setBookingResult] = useState(null);
  const [error, setError] = useState('');

  const content = {
    en: {
      title: "Book a Session",
      watchIntro: "Watch Introduction",
      skipToBooking: "Skip to Booking",
      selectDateTime: "Select Date & Time",
      selectDate: "Select Date",
      selectTime: "Select Time Slot",
      contactInfo: "Your Contact Information",
      name: "Full Name",
      email: "Email Address",
      phone: "Phone Number (optional)",
      notes: "Notes (optional)",
      sessionType: "Session Type",
      trial: "Trial Session (Free)",
      oneOnOne: "1-on-1 Private Lesson",
      group: "Group Session",
      next: "Next",
      back: "Back",
      book: "Book Now",
      success: "Booking Submitted!",
      successMessage: "Your booking request has been submitted. You'll receive a confirmation email shortly.",
      close: "Close",
      noAvailability: "No available slots",
      unavailable: "Unavailable"
    },
    ar: {
      title: "احجز جلسة",
      watchIntro: "شاهد التعريف",
      skipToBooking: "تخطى للحجز",
      selectDateTime: "اختر التاريخ والوقت",
      selectDate: "اختر التاريخ",
      selectTime: "اختر الوقت",
      contactInfo: "معلومات الاتصال الخاصة بك",
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      phone: "رقم الهاتف (اختياري)",
      notes: "ملاحظات (اختياري)",
      sessionType: "نوع الجلسة",
      trial: "جلسة تجريبية (مجانية)",
      oneOnOne: "درس خاص 1-1",
      group: "جلسة جماعية",
      next: "التالي",
      back: "رجوع",
      book: "احجز الآن",
      success: "تم إرسال الحجز!",
      successMessage: "تم إرسال طلب الحجز الخاص بك. ستتلقى رسالة تأكيد قريباً.",
      close: "إغلاق",
      noAvailability: "لا توجد فترات متاحة",
      unavailable: "غير متاح"
    }
  };

  const t = content[language];

  useEffect(() => {
    if (open && teacher?.id) {
      fetchAvailability();
    }
  }, [open, teacher]);

  useEffect(() => {
    if (selectedDate && teacher?.id) {
      fetchBookedSlots();
    }
  }, [selectedDate, teacher]);

  const fetchAvailability = async () => {
    try {
      const response = await bookingAPI.getTeacherAvailability(teacher.id);
      setAvailability(response.data);
    } catch (err) {
      console.error('Failed to fetch availability:', err);
    }
  };

  const fetchBookedSlots = async () => {
    if (!selectedDate) return;
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await bookingAPI.getBookedSlots(teacher.id, dateStr, dateStr);
      setBookedSlots(response.data.booked_slots || []);
    } catch (err) {
      console.error('Failed to fetch booked slots:', err);
    }
  };

  const getAvailableTimesForDate = (date) => {
    if (!availability || !date) return [];
    
    const dayOfWeek = getDay(date);
    // Convert Sunday=0 to Monday=0 format
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    const daySlot = availability.slots?.find(s => s.day_of_week === adjustedDay && s.is_available);
    if (!daySlot) return [];
    
    // Generate time slots (30 min intervals)
    const times = [];
    const [startHour] = daySlot.start_time.split(':').map(Number);
    const [endHour] = daySlot.end_time.split(':').map(Number);
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let min of ['00', '30']) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${min}`;
        // Check if already booked
        const isBooked = bookedSlots.some(b => b.booking_time === timeStr);
        times.push({ time: timeStr, booked: isBooked });
      }
    }
    
    return times;
  };

  const isDateAvailable = (date) => {
    if (!availability) return false;
    const dayOfWeek = getDay(date);
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    return availability.slots?.some(s => s.day_of_week === adjustedDay && s.is_available);
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    
    try {
      const response = await bookingAPI.bookSession({
        teacher_id: teacher.id,
        student_name: studentName,
        student_email: studentEmail,
        student_phone: studentPhone || null,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        booking_time: selectedTime,
        session_type: sessionType,
        notes: notes || null
      });
      
      setBookingResult(response.data);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime('');
    setStudentName('');
    setStudentEmail('');
    setStudentPhone('');
    setNotes('');
    setBookingResult(null);
    setError('');
    onClose();
  };

  const availableTimes = getAvailableTimesForDate(selectedDate);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-teal-600" />
            {t.title} - {teacher?.name}
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Intro Video */}
        {step === 1 && (
          <div className="space-y-4 py-4">
            {teacher?.intro_video_url ? (
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                <iframe
                  src={teacher.intro_video_url.replace('watch?v=', 'embed/')}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`${teacher.name} Introduction`}
                />
              </div>
            ) : (
              <div className="aspect-video bg-gradient-to-br from-teal-50 to-emerald-50 rounded-lg flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <Video className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No intro video available</p>
                </div>
              </div>
            )}
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900">{teacher?.name}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {language === 'ar' ? teacher?.bio_ar : teacher?.bio}
              </p>
            </div>

            <div className="flex gap-3">
              {teacher?.intro_video_url && (
                <Button variant="outline" className="flex-1" onClick={() => window.open(teacher.intro_video_url, '_blank')}>
                  <Play className="w-4 h-4 mr-2" />
                  {t.watchIntro}
                </Button>
              )}
              <Button className="flex-1 bg-teal-600 hover:bg-teal-700" onClick={() => setStep(2)}>
                {t.skipToBooking}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Select Date & Time */}
        {step === 2 && (
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-base font-medium">{t.selectDate}</Label>
              <div className="mt-2">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date() || !isDateAvailable(date)}
                  className="rounded-md border"
                />
              </div>
            </div>

            {selectedDate && (
              <div>
                <Label className="text-base font-medium">{t.selectTime}</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {availableTimes.length > 0 ? (
                    availableTimes.map(({ time, booked }) => (
                      <Button
                        key={time}
                        type="button"
                        variant={selectedTime === time ? 'default' : 'outline'}
                        disabled={booked}
                        onClick={() => setSelectedTime(time)}
                        className={`text-sm ${selectedTime === time ? 'bg-teal-600' : ''} ${booked ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {time}
                      </Button>
                    ))
                  ) : (
                    <p className="col-span-4 text-gray-500 text-center py-4">{t.noAvailability}</p>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>
            )}

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>{t.back}</Button>
              <Button 
                className="flex-1 bg-teal-600 hover:bg-teal-700" 
                onClick={() => setStep(3)}
                disabled={!selectedDate || !selectedTime}
              >
                {t.next}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Contact Info */}
        {step === 3 && (
          <div className="space-y-4 py-4">
            <div className="bg-teal-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-teal-700">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')} at {selectedTime}
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="sessionType">{t.sessionType}</Label>
                <select
                  id="sessionType"
                  value={sessionType}
                  onChange={(e) => setSessionType(e.target.value)}
                  className="w-full mt-1 p-2 border rounded-md"
                >
                  <option value="trial">{t.trial}</option>
                  <option value="one_on_one">{t.oneOnOne}</option>
                  <option value="group">{t.group}</option>
                </select>
              </div>

              <div>
                <Label htmlFor="studentName">{t.name} *</Label>
                <Input
                  id="studentName"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="John Smith"
                  required
                />
              </div>

              <div>
                <Label htmlFor="studentEmail">{t.email} *</Label>
                <Input
                  id="studentEmail"
                  type="email"
                  value={studentEmail}
                  onChange={(e) => setStudentEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <Label htmlFor="studentPhone">{t.phone}</Label>
                <Input
                  id="studentPhone"
                  value={studentPhone}
                  onChange={(e) => setStudentPhone(e.target.value)}
                  placeholder="+1234567890"
                />
              </div>

              <div>
                <Label htmlFor="notes">{t.notes}</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Any specific topics you'd like to discuss..."
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>
            )}

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>{t.back}</Button>
              <Button 
                className="flex-1 bg-teal-600 hover:bg-teal-700" 
                onClick={handleSubmit}
                disabled={loading || !studentName || !studentEmail}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t.book}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t.success}</h3>
            <p className="text-gray-600 mb-6">{t.successMessage}</p>
            <Button onClick={handleClose} className="bg-teal-600 hover:bg-teal-700">
              {t.close}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TeacherBookingModal;
